// @flow
import React, { useEffect } from "react";
import { Router } from "react-router-dom";
import { AppProvider } from "@shopify/polaris";
import translations from "@shopify/polaris/locales/en.json";
import Intercom from "@intercom/messenger-js-sdk";

import { shopActions } from "store/shopStore";
import { storeProvide, storeConnect } from "appStore";
import makeRequestProps from "store/makeRequestProps";
import { IndexSkeletonPage } from "components/skeletons";
import { reportError } from "lib/errorHelpers";

import ErrorBoundary from "components/ErrorBoundary";
import Routes from "./Routes";
import ErrorPage from "components/ErrorPage";
import history from "lib/history";
import NavigationMenu from "components/NavigationMenu";

import Navigation from "contexts/Navigation";
import { FlagsProvider } from "contexts/Flags";
import { I18nProvider } from "ui/src/providers/I18nProvider.tsx";
import GraphQLProvider from "graphql/provider";
import useToast from "hooks/useToast";
import { setupBackNavigationSaveBarHandler } from "components/SaveBar";

import type { ShopRequest } from "appStore";
import type { Shop } from "types/flow-types/shop";
import type { Toast } from "appStore";

import "@shopify/polaris/build/esm/styles.css";
import "./App.css";

type Props = {
  shopRequest: ShopRequest,
  checkCompanionApps: () => void,
  clearToast: () => void,
  initShop: () => void,
  toast: Toast,
  shop: Shop
};
function AppWrapper(props) {
  return (
    <Router history={history}>
      <I18nProvider>
        <AppProvider i18n={translations}>
          <ErrorBoundary>
            <GraphQLProvider>
              <App {...props} />
            </GraphQLProvider>
          </ErrorBoundary>
        </AppProvider>
      </I18nProvider>
    </Router>
  );
}

export const App = (props: Props) => {
  useEffect(() => {
    const { initShop } = props;

    /**
     * Sentry is supposed to handle these errors out of the box – perhaps the
     * iframe prevents them from bubbling up?
     **/
    window.addEventListener("unhandledrejection", error => {
      reportError(error.reason);
    });

    // Hide the Zendesk Web Widget until feature flags are available
    window.zE && window.zE("webWidget", "hide");

    // Setup handler to hide SaveBar on browser back navigation (hook into app history)
    setupBackNavigationSaveBarHandler(history);

    initShop();
  }, []);

  useEffect(() => {
    const { flags, settings } = props.shopRequest.shop;
    const { email, shop_owner: shopOwner } = settings;

    if (!flags.enableZendeskWidget || !window.zE) {
      return;
    }

    window.zE("webWidget", "show");
    window.zE("webWidget", "identify", { name: shopOwner, email: email });
    window.zE("webWidget", "prefill", {
      email: {
        value: email,
        readOnly: false // user can edit the value on the form
      }
    });
  }, [props.shopRequest.shop.flags, window.zE]);

  useEffect(() => {
    const { settings } = props.shopRequest.shop;

    const intercomAppId = process.env.REACT_APP_INTERCOM_APP_ID;

    if (intercomAppId && settings.myshopify_domain) {
      Intercom({
        app_id: intercomAppId,
        user_id: settings.myshopify_domain
      });
    }
  }, [props.shopRequest.shop]);

  const { shopRequest, clearToast, toast } = props;
  const { error, loading, shop, status } = shopRequest;
  const { showToast } = useToast();

  useEffect(() => {
    toast &&
      showToast({
        error: toast.error,
        message: toast.content,
        onDismiss: clearToast
      });
  }, [toast]);

  if (loading) {
    return <IndexSkeletonPage />;
  }

  if (error) {
    return <ErrorPage stateKey="appConfig" status={status} />;
  }

  const { flags } = shop;

  return (
    <>
      <Navigation>
        <FlagsProvider value={flags}>
          <NavigationMenu />
          <Routes {...props} />
        </FlagsProvider>
      </Navigation>
    </>
  );
};

const mapStateToProps = state => {
  return {
    shopRequest: makeRequestProps(state, "shop"),
    toast: state.toast
  };
};

export default storeProvide(
  storeConnect(AppWrapper, mapStateToProps, shopActions)
);
